import React from 'react';
import '../assets/Documentation.css';
import { API_PORTAL_URL } from '../constants/links';

/**
 * MailDoc - Documentation de l'API d'envoi d'emails
 * 
 * Cette API vous permet d'envoyer facilement des emails de notification
 * pour vos applications de connexion (Login Apps). Elle est conçue pour être
 * simple à utiliser et hautement fiable.
 * 
 * Cas d'usage principaux :
 * - Notifications de connexion (login/logout)
 * - Emails de vérification d'identité
 * - Alertes de sécurité (connexion suspecte, changement de mot de passe)
 * - Codes OTP par email
 * - Confirmations d'actions sensibles
 * - Notifications d'activité utilisateur
 * 
 * @component
 * @author WonyaSoft Team
 * @version 2.0
 */
const MailDoc = () => {
    /**
     * Copie le code d'exemple dans le presse-papiers
     * @param {string} code - Le code à copier
     */
    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        alert('Code copié dans le presse-papiers !');
    };

    return (
        <div className="mail-doc-page" style={{
            minHeight: '100vh',
            background: '#0f172a',
            backgroundImage: 'radial-gradient(circle at 15% 20%, rgba(220,53,69,0.35), transparent 45%), radial-gradient(circle at 85% 10%, rgba(248,113,113,0.25), transparent 40%), radial-gradient(circle at 50% 75%, rgba(220,53,69,0.4), transparent 55%)',
            padding: '40px 20px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(15,23,42,0.2))'
            }} />
            <div style={{
                position: 'absolute',
                top: '-120px',
                right: '-80px',
                width: '360px',
                height: '360px',
                background: 'radial-gradient(circle, rgba(220,53,69,0.55), transparent 70%)',
                filter: 'blur(40px)'
            }} />
            <div style={{
                position: 'absolute',
                bottom: '-160px',
                left: '-120px',
                width: '420px',
                height: '420px',
                background: 'radial-gradient(circle, rgba(248,113,113,0.35), transparent 75%)',
                filter: 'blur(60px)'
            }} />

            <div style={{position: 'relative', zIndex: 1}}>
            {/* Header avec bouton retour */}
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto 30px',
                display: 'flex',
                alignItems: 'center',
                gap: '20px'
            }}>
                <button
                    onClick={() => {
                        window.location.href = API_PORTAL_URL;
                    }}
                    style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: '2px solid rgba(255,255,255,0.3)',
                        color: '#fff',
                        padding: '12px 24px',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backdropFilter: 'blur(10px)'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.3)';
                        e.target.style.transform = 'translateX(-5px)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.2)';
                        e.target.style.transform = 'translateX(0)';
                    }}
                >
                    <span>←</span>
                    <span>Retour à la documentation</span>
                </button>
            </div>

            {/* Container principal */}
            <div className="mail-doc-card" style={{
                maxWidth: '1200px',
                margin: '0 auto',
                background: '#fff',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
                {/* En-tête */}
                <div style={{textAlign: 'center', marginBottom: '50px'}}>
                    <span style={{fontSize: '80px', display: 'block', marginBottom: '20px'}}>📧</span>
                    <h1 style={{
                        fontSize: '42px',
                        fontWeight: '700',
                        color: '#2c3e50',
                        marginBottom: '16px',
                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}>
                        API Mail - Notifications pour vos Login Apps
                    </h1>
                    <p style={{
                        fontSize: '18px',
                        color: '#6c757d',
                        maxWidth: '800px',
                        margin: '0 auto 20px',
                        lineHeight: '1.6',
                        fontWeight: '500'
                    }}>
                        🚀 API simple et puissante pour envoyer des emails de notification de connexion
                    </p>
                    <div style={{
                        display: 'flex',
                        gap: '15px',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        marginTop: '25px'
                    }}>
                        <span className="mail-doc-status-badge mail-doc-status-badge--success" style={{
                            background: 'linear-gradient(135deg, #28a745, #20c997)',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: '25px',
                            fontSize: '14px',
                            fontWeight: '600',
                            boxShadow: '0 4px 12px rgba(40,167,69,0.3)'
                        }}>✅ Intégration en 5 minutes</span>
                        <span style={{
                            background: 'linear-gradient(135deg, #007bff, #0056b3)',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: '25px',
                            fontSize: '14px',
                            fontWeight: '600',
                            boxShadow: '0 4px 12px rgba(0,123,255,0.3)'
                        }}>🔒 Sécurisé & Fiable</span>
                        <span style={{
                            background: 'linear-gradient(135deg, #6f42c1, #5a32a3)',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: '25px',
                            fontSize: '14px',
                            fontWeight: '600',
                            boxShadow: '0 4px 12px rgba(111,66,193,0.3)'
                        }}>⚡ Ultra Rapide</span>
                        <span style={{
                            background: 'linear-gradient(135deg, #fd7e14, #e8590c)',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: '25px',
                            fontSize: '14px',
                            fontWeight: '600',
                            boxShadow: '0 4px 12px rgba(253,126,20,0.3)'
                        }}>📊 Suivi en Temps Réel</span>
                    </div>
                </div>

                {/* Section Avantages Clés */}
                <section style={{marginBottom: '50px'}}>
                    <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '20px',
                        padding: '40px',
                        color: '#fff',
                        boxShadow: '0 15px 40px rgba(102,126,234,0.3)'
                    }}>
                        <h2 style={{
                            fontSize: '32px',
                            fontWeight: '700',
                            textAlign: 'center',
                            marginBottom: '35px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '15px'
                        }}>
                            <span style={{fontSize: '40px'}}>🎯</span>
                            <span>Pourquoi utiliser notre API Mail ?</span>
                        </h2>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: '25px'
                        }}>
                            {/* Avantage 1 */}
                            <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                borderRadius: '16px',
                                padding: '25px',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.2)'
                            }}>
                                <div style={{fontSize: '50px', marginBottom: '15px'}}>🔐</div>
                                <h3 style={{fontSize: '20px', fontWeight: '700', marginBottom: '12px'}}>
                                    Authentification Sécurisée
                                </h3>
                                <p style={{fontSize: '15px', lineHeight: '1.6', opacity: 0.95}}>
                                    Envoyez des notifications de connexion en temps réel avec OTP, 
                                    codes de vérification et alertes de sécurité pour protéger vos utilisateurs.
                                </p>
                            </div>

                            {/* Avantage 2 */}
                            <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                borderRadius: '16px',
                                padding: '25px',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.2)'
                            }}>
                                <div style={{fontSize: '50px', marginBottom: '15px'}}>⚡</div>
                                <h3 style={{fontSize: '20px', fontWeight: '700', marginBottom: '12px'}}>
                                    Livraison Instantanée
                                </h3>
                                <p style={{fontSize: '15px', lineHeight: '1.6', opacity: 0.95}}>
                                    Taux de livraison de 99.9% avec une latence moyenne de moins de 2 secondes. 
                                    Vos emails arrivent instantanément dans la boîte de réception.
                                </p>
                            </div>

                            {/* Avantage 3 */}
                            <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                borderRadius: '16px',
                                padding: '25px',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.2)'
                            }}>
                                <div style={{fontSize: '50px', marginBottom: '15px'}}>🎨</div>
                                <h3 style={{fontSize: '20px', fontWeight: '700', marginBottom: '12px'}}>
                                    HTML Personnalisé
                                </h3>
                                <p style={{fontSize: '15px', lineHeight: '1.6', opacity: 0.95}}>
                                    Support complet HTML/CSS pour créer des emails magnifiques et professionnels 
                                    adaptés à votre marque avec des templates personnalisables.
                                </p>
                            </div>

                            {/* Avantage 4 */}
                            <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                borderRadius: '16px',
                                padding: '25px',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.2)'
                            }}>
                                <div style={{fontSize: '50px', marginBottom: '15px'}}>📊</div>
                                <h3 style={{fontSize: '20px', fontWeight: '700', marginBottom: '12px'}}>
                                    Traçabilité Complète
                                </h3>
                                <p style={{fontSize: '15px', lineHeight: '1.6', opacity: 0.95}}>
                                    Historique détaillé de tous vos envois avec statuts, horodatage et métriques 
                                    pour un suivi et une analyse optimale de vos campagnes.
                                </p>
                            </div>

                            {/* Avantage 5 */}
                            <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                borderRadius: '16px',
                                padding: '25px',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.2)'
                            }}>
                                <div style={{fontSize: '50px', marginBottom: '15px'}}>💰</div>
                                <h3 style={{fontSize: '20px', fontWeight: '700', marginBottom: '12px'}}>
                                    Tarification Simple
                                </h3>
                                <p style={{fontSize: '15px', lineHeight: '1.6', opacity: 0.95}}>
                                    Pas de frais cachés ! Rechargez votre solde et payez uniquement pour 
                                    les emails envoyés. Système de crédit transparent et économique.
                                </p>
                            </div>

                            {/* Avantage 6 */}
                            <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                borderRadius: '16px',
                                padding: '25px',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.2)'
                            }}>
                                <div style={{fontSize: '50px', marginBottom: '15px'}}>🛠️</div>
                                <h3 style={{fontSize: '20px', fontWeight: '700', marginBottom: '12px'}}>
                                    Intégration Facile
                                </h3>
                                <p style={{fontSize: '15px', lineHeight: '1.6', opacity: 0.95}}>
                                    API RESTful simple avec une seule requête POST. Compatible avec tous 
                                    les langages de programmation. Documentation claire et exemples complets.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Cas d'usage */}
                <section style={{marginBottom: '50px'}}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        marginBottom: '24px',
                        paddingBottom: '16px',
                        borderBottom: '3px solid #dc3545'
                    }}>
                        <span style={{fontSize: '40px'}}>💡</span>
                        <h2 style={{fontSize: '32px', fontWeight: '700', color: '#2c3e50', margin: 0}}>
                            Cas d'Usage Principaux
                        </h2>
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '20px'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            color: '#fff',
                            boxShadow: '0 8px 24px rgba(240,147,251,0.3)'
                        }}>
                            <div style={{fontSize: '48px', marginBottom: '15px'}}>🔑</div>
                            <h3 style={{fontSize: '22px', fontWeight: '700', marginBottom: '12px'}}>
                                Notifications de Login
                            </h3>
                            <p style={{fontSize: '15px', lineHeight: '1.7', opacity: 0.95}}>
                                Alertez vos utilisateurs à chaque connexion : localisation, appareil, 
                                adresse IP et horodatage pour une sécurité maximale.
                            </p>
                        </div>

                        <div style={{
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            color: '#fff',
                            boxShadow: '0 8px 24px rgba(79,172,254,0.3)'
                        }}>
                            <div style={{fontSize: '48px', marginBottom: '15px'}}>🔢</div>
                            <h3 style={{fontSize: '22px', fontWeight: '700', marginBottom: '12px'}}>
                                Codes OTP & 2FA
                            </h3>
                            <p style={{fontSize: '15px', lineHeight: '1.7', opacity: 0.95}}>
                                Envoyez des codes de vérification à usage unique pour l'authentification 
                                à deux facteurs et la validation d'identité.
                            </p>
                        </div>

                        <div style={{
                            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            color: '#fff',
                            boxShadow: '0 8px 24px rgba(250,112,154,0.3)'
                        }}>
                            <div style={{fontSize: '48px', marginBottom: '15px'}}>⚠️</div>
                            <h3 style={{fontSize: '22px', fontWeight: '700', marginBottom: '12px'}}>
                                Alertes de Sécurité
                            </h3>
                            <p style={{fontSize: '15px', lineHeight: '1.7', opacity: 0.95}}>
                                Informez immédiatement sur les activités suspectes : tentatives de 
                                connexion échouées, changements de mot de passe, etc.
                            </p>
                        </div>

                        <div style={{
                            background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            color: '#fff',
                            boxShadow: '0 8px 24px rgba(48,207,208,0.3)'
                        }}>
                            <div style={{fontSize: '48px', marginBottom: '15px'}}>✉️</div>
                            <h3 style={{fontSize: '22px', fontWeight: '700', marginBottom: '12px'}}>
                                Emails de Bienvenue
                            </h3>
                            <p style={{fontSize: '15px', lineHeight: '1.7', opacity: 0.95}}>
                                Accueillez vos nouveaux utilisateurs avec un email personnalisé 
                                contenant les informations essentielles et le lien de confirmation.
                            </p>
                        </div>

                        <div style={{
                            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            color: '#2c3e50',
                            boxShadow: '0 8px 24px rgba(168,237,234,0.3)'
                        }}>
                            <div style={{fontSize: '48px', marginBottom: '15px'}}>🔄</div>
                            <h3 style={{fontSize: '22px', fontWeight: '700', marginBottom: '12px'}}>
                                Réinitialisation de Mot de Passe
                            </h3>
                            <p style={{fontSize: '15px', lineHeight: '1.7'}}>
                                Envoyez des liens sécurisés avec expiration automatique pour 
                                permettre aux utilisateurs de réinitialiser leur mot de passe.
                            </p>
                        </div>

                        <div style={{
                            background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            color: '#2c3e50',
                            boxShadow: '0 8px 24px rgba(255,236,210,0.3)'
                        }}>
                            <div style={{fontSize: '48px', marginBottom: '15px'}}>📢</div>
                            <h3 style={{fontSize: '22px', fontWeight: '700', marginBottom: '12px'}}>
                                Notifications d'Activité
                            </h3>
                            <p style={{fontSize: '15px', lineHeight: '1.7'}}>
                                Tenez vos utilisateurs informés : nouvelles fonctionnalités, mises à jour 
                                importantes, confirmations de transactions et bien plus encore.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Endpoint */}
                <section style={{marginBottom: '50px'}}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        marginBottom: '24px',
                        paddingBottom: '16px',
                        borderBottom: '3px solid #dc3545'
                    }}>
                        <span style={{fontSize: '40px'}}>🎯</span>
                        <h2 style={{fontSize: '32px', fontWeight: '700', color: '#2c3e50', margin: 0}}>
                            Point d'Accès API (Endpoint)
                        </h2>
                    </div>
                    <div style={{
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        borderRadius: '16px',
                        padding: '30px',
                        border: '2px solid #dee2e6'
                    }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px'}}>
                            <span style={{
                                background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                color: '#fff',
                                padding: '10px 20px',
                                borderRadius: '10px',
                                fontSize: '16px',
                                fontWeight: '700',
                                boxShadow: '0 4px 12px rgba(220,53,69,0.3)'
                            }}>POST</span>
                            <span className="mail-doc-status-badge mail-doc-status-badge--rest" style={{
                                background: '#28a745',
                                color: '#fff',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: '600'
                            }}>REST API</span>
                        </div>
                        <code style={{
                            display: 'block',
                            background: '#2c3e50',
                            color: '#fff',
                            padding: '20px 24px',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                            lineHeight: '1.6',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}>
                            https://api.wonyasoft.com/sendInfoMail
                        </code>
                        <div style={{
                            marginTop: '20px',
                            padding: '15px',
                            background: 'rgba(40,167,69,0.1)',
                            borderLeft: '4px solid #28a745',
                            borderRadius: '8px'
                        }}>
                            <p style={{
                                margin: 0,
                                fontSize: '14px',
                                color: '#155724',
                                fontWeight: '600'
                            }}>
                                💡 <strong>Astuce :</strong> Cette API fonctionne avec toutes les plateformes et tous 
                                les langages de programmation (JavaScript, Python, PHP, Java, C#, etc.)
                            </p>
                        </div>
                    </div>
                </section>

                {/* Paramètres */}
                <section style={{marginBottom: '50px'}}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        marginBottom: '24px',
                        paddingBottom: '16px',
                        borderBottom: '3px solid #dc3545'
                    }}>
                        <span style={{fontSize: '40px'}}>📋</span>
                        <h2 style={{fontSize: '32px', fontWeight: '700', color: '#2c3e50', margin: 0}}>
                            Paramètres requis
                        </h2>
                    </div>
                    <div style={{
                        background: '#fff',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '2px solid #dee2e6',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}>
                        <table className="mail-doc-params-table" style={{width: '100%', borderCollapse: 'collapse'}}>
                            <thead>
                                <tr style={{
                                    background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                    color: '#fff'
                                }}>
                                    <th style={{padding: '18px 20px', textAlign: 'left', fontSize: '15px', fontWeight: '700'}}>Paramètre</th>
                                    <th style={{padding: '18px 20px', textAlign: 'left', fontSize: '15px', fontWeight: '700'}}>Type</th>
                                    <th style={{padding: '18px 20px', textAlign: 'left', fontSize: '15px', fontWeight: '700'}}>Description</th>
                                    <th style={{padding: '18px 20px', textAlign: 'left', fontSize: '15px', fontWeight: '700'}}>Exemple</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style={{borderBottom: '1px solid #e9ecef', background: '#fff'}}>
                                    <td style={{padding: '18px 20px', fontSize: '15px', fontFamily: 'monospace', fontWeight: '600', color: '#dc3545'}}>RefPartenaire</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', color: '#6c757d'}}>string</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', color: '#495057'}}>Identifiant de votre caisse API</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', fontFamily: 'monospace', color: '#2c3e50'}}>"AA082"</td>
                                </tr>
                                <tr style={{borderBottom: '1px solid #e9ecef', background: '#f8f9fa'}}>
                                    <td style={{padding: '18px 20px', fontSize: '15px', fontFamily: 'monospace', fontWeight: '600', color: '#dc3545'}}>TokenMail</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', color: '#6c757d'}}>string</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', color: '#495057'}}>Token d'authentification Mail</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', fontFamily: 'monospace', color: '#2c3e50'}}>"VOTRE_TOKEN_MAIL..."</td>
                                </tr>
                                <tr style={{borderBottom: '1px solid #e9ecef', background: '#fff'}}>
                                    <td style={{padding: '18px 20px', fontSize: '15px', fontFamily: 'monospace', fontWeight: '600', color: '#dc3545'}}>DestMail</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', color: '#6c757d'}}>string</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', color: '#495057'}}>Adresse email du destinataire</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', fontFamily: 'monospace', color: '#2c3e50'}}>"user@example.com"</td>
                                </tr>
                                <tr style={{borderBottom: '1px solid #e9ecef', background: '#f8f9fa'}}>
                                    <td style={{padding: '18px 20px', fontSize: '15px', fontFamily: 'monospace', fontWeight: '600', color: '#dc3545'}}>Objet</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', color: '#6c757d'}}>string</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', color: '#495057'}}>Objet/Sujet de l'email</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', fontFamily: 'monospace', color: '#2c3e50'}}>"Bienvenue"</td>
                                </tr>
                                <tr style={{background: '#fff'}}>
                                    <td style={{padding: '18px 20px', fontSize: '15px', fontFamily: 'monospace', fontWeight: '600', color: '#dc3545'}}>BodyMail</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', color: '#6c757d'}}>string</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', color: '#495057'}}>Corps de l'email (HTML supporté)</td>
                                    <td style={{padding: '18px 20px', fontSize: '14px', fontFamily: 'monospace', color: '#2c3e50'}}>"&lt;h1&gt;Hello&lt;/h1&gt;"</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Exemple de requête */}
                <section style={{marginBottom: '50px'}}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        marginBottom: '24px',
                        paddingBottom: '16px',
                        borderBottom: '3px solid #dc3545'
                    }}>
                        <span style={{fontSize: '40px'}}>💻</span>
                        <h2 style={{fontSize: '32px', fontWeight: '700', color: '#2c3e50', margin: 0}}>
                            Exemple de requête
                        </h2>
                    </div>
                    <div style={{
                        background: '#2c3e50',
                        borderRadius: '16px',
                        padding: '30px',
                        position: 'relative',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px'
                        }}>
                            <span className="mail-doc-code-label" style={{color: '#10b981', fontSize: '14px', fontWeight: '700'}}>JSON Request Body</span>
                            <button
                                onClick={() => copyCode(`{
  "RefPartenaire": "AA082",
  "TokenMail": "VOTRE_TOKEN_MAIL",
  "DestMail": "email@example.com",
  "Objet": "Info ok",
  "BodyMail": "Votre message ici en HTML"
}`)}
                                style={{
                                    background: '#dc3545',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#c82333'}
                                onMouseLeave={(e) => e.target.style.background = '#dc3545'}
                            >
                                📋 Copier
                            </button>
                        </div>
                        <pre style={{
                            color: '#fff',
                            fontSize: '15px',
                            fontFamily: 'monospace',
                            margin: 0,
                            overflowX: 'auto',
                            lineHeight: '1.8'
                        }}>
{`{
  "RefPartenaire": "AA082",
  "TokenMail": "VOTRE_TOKEN_MAIL",
  "DestMail": "email@example.com",
  "Objet": "Info ok",
  "BodyMail": "Votre message ici en HTML"
}`}
                        </pre>
                    </div>
                </section>

                {/* Exemples de code par langage (Production 2026) */}
                <section style={{marginBottom: '50px'}}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        marginBottom: '24px',
                        paddingBottom: '16px',
                        borderBottom: '3px solid #dc3545'
                    }}>
                        <span style={{fontSize: '40px'}}>💻</span>
                        <h2 style={{fontSize: '32px', fontWeight: '700', color: '#2c3e50', margin: 0}}>
                            Exemples de code par langage (Production 2026)
                        </h2>
                    </div>

                    <div style={{
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '30px',
                        border: '2px solid #2196f3'
                    }}>
                        <p style={{
                            margin: 0,
                            fontSize: '15px',
                            color: '#0d47a1',
                            fontWeight: '600',
                            lineHeight: '1.8'
                        }}>
                            🚀 <strong>Standards Production 2026 :</strong> Tous les exemples incluent gestion d'erreur complète (try/catch, exceptions spécifiques), timeout de 15 secondes, validation de réponse (status === 'S'), logging structuré avec préfixe [MAIL], headers HTTP complets et typage strict.
                        </p>
                    </div>

                    {/* PHP 8.2+ */}
                    <div style={{marginBottom: '30px'}}>
                        <h3 style={{
                            fontSize: '22px',
                            fontWeight: '700',
                            color: '#2c3e50',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{fontSize: '28px'}}>🐘</span>
                            <span>PHP 8.2+ (Production)</span>
                        </h3>
                        <div style={{
                            background: '#2c3e50',
                            borderRadius: '12px',
                            padding: '24px',
                            position: 'relative',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}>
                            <button
                                onClick={() => copyCode(`<?php
declare(strict_types=1);

$url = 'https://api.wonyasoft.com/sendInfoMail';
$data = [
    'RefPartenaire' => 'AA082',
    'TokenMail' => 'VOTRE_TOKEN_MAIL',
    'DestMail' => 'user@example.com',
    'Objet' => 'Notification de connexion',
    'BodyMail' => '<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>'
];

try {
    $context = stream_context_create([
        'http' => [
            'method' => 'POST',
            'header' => 'Content-Type: application/json',
            'content' => json_encode($data, JSON_THROW_ON_ERROR),
            'timeout' => 15
        ]
    ]);
    
    $response = file_get_contents($url, false, $context);
    if ($response === false) {
        throw new RuntimeException('Échec de la requête HTTP');
    }
    
    $result = json_decode($response, true, 512, JSON_THROW_ON_ERROR);
    
    if (isset($result['status']) && $result['status'] === 'S') {
        error_log("[MAIL] Email envoyé avec succès - ID: {$result['id']}");
        echo "Email envoyé à {$result['DestMail']}\\n";
    } else {
        error_log("[MAIL] Échec envoi - Réponse: " . json_encode($result));
        throw new RuntimeException('Statut invalide reçu');
    }
} catch (Throwable $e) {
    error_log("[MAIL] ERREUR: " . $e->getMessage());
    http_response_code(500);
    exit(1);
}`)}
                                style={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                    background: '#dc3545',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                📋 Copier
                            </button>
                            <pre style={{
                                color: '#fff',
                                fontSize: '14px',
                                fontFamily: 'monospace',
                                margin: 0,
                                overflowX: 'auto',
                                lineHeight: '1.6'
                            }}>
{`<?php
declare(strict_types=1);

$url = 'https://api.wonyasoft.com/sendInfoMail';
$data = [
    'RefPartenaire' => 'AA082',
    'TokenMail' => 'VOTRE_TOKEN_MAIL',
    'DestMail' => 'user@example.com',
    'Objet' => 'Notification de connexion',
    'BodyMail' => '<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>'
];

try {
    $context = stream_context_create([
        'http' => [
            'method' => 'POST',
            'header' => 'Content-Type: application/json',
            'content' => json_encode($data, JSON_THROW_ON_ERROR),
            'timeout' => 15
        ]
    ]);
    
    $response = file_get_contents($url, false, $context);
    if ($response === false) {
        throw new RuntimeException('Échec de la requête HTTP');
    }
    
    $result = json_decode($response, true, 512, JSON_THROW_ON_ERROR);
    
    if (isset($result['status']) && $result['status'] === 'S') {
        error_log("[MAIL] Email envoyé avec succès - ID: {$result['id']}");
        echo "Email envoyé à {$result['DestMail']}\\n";
    } else {
        error_log("[MAIL] Échec envoi - Réponse: " . json_encode($result));
        throw new RuntimeException('Statut invalide reçu');
    }
} catch (Throwable $e) {
    error_log("[MAIL] ERREUR: " . $e->getMessage());
    http_response_code(500);
    exit(1);
}`}
                            </pre>
                        </div>
                    </div>

                    {/* React Native / JavaScript */}
                    <div style={{marginBottom: '30px'}}>
                        <h3 style={{
                            fontSize: '22px',
                            fontWeight: '700',
                            color: '#2c3e50',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{fontSize: '28px'}}>⚛️</span>
                            <span>React Native / JavaScript (Production)</span>
                        </h3>
                        <div style={{
                            background: '#2c3e50',
                            borderRadius: '12px',
                            padding: '24px',
                            position: 'relative',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}>
                            <button
                                onClick={() => copyCode(`const sendEmail = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);
  
  const data = {
    RefPartenaire: 'AA082',
    TokenMail: 'VOTRE_TOKEN_MAIL',
    DestMail: 'user@example.com',
    Objet: 'Notification de connexion',
    BodyMail: '<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>'
  };

  try {
    const response = await fetch('https://api.wonyasoft.com/sendInfoMail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': 'MyApp/2.0'
      },
      body: JSON.stringify(data),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(\`HTTP \${response.status}: \${response.statusText}\`);
    }

    const result = await response.json();

    if (result.status === 'S') {
      console.log(\`[MAIL] Email envoyé avec succès - ID: \${result.id}\`);
      console.log(\`[MAIL] Destinataire: \${result.DestMail}\`);
      return result;
    } else {
      console.error('[MAIL] Échec envoi:', result);
      throw new Error('Statut invalide reçu');
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('[MAIL] TIMEOUT: Requête annulée après 15s');
    } else {
      console.error('[MAIL] ERREUR:', error.message);
    }
    throw error;
  }
};`)}
                                style={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                    background: '#dc3545',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                📋 Copier
                            </button>
                            <pre style={{
                                color: '#fff',
                                fontSize: '14px',
                                fontFamily: 'monospace',
                                margin: 0,
                                overflowX: 'auto',
                                lineHeight: '1.6'
                            }}>
{`const sendEmail = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);
  
  const data = {
    RefPartenaire: 'AA082',
    TokenMail: 'VOTRE_TOKEN_MAIL',
    DestMail: 'user@example.com',
    Objet: 'Notification de connexion',
    BodyMail: '<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>'
  };

  try {
    const response = await fetch('https://api.wonyasoft.com/sendInfoMail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': 'MyApp/2.0'
      },
      body: JSON.stringify(data),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(\`HTTP \${response.status}: \${response.statusText}\`);
    }

    const result = await response.json();

    if (result.status === 'S') {
      console.log(\`[MAIL] Email envoyé avec succès - ID: \${result.id}\`);
      console.log(\`[MAIL] Destinataire: \${result.DestMail}\`);
      return result;
    } else {
      console.error('[MAIL] Échec envoi:', result);
      throw new Error('Statut invalide reçu');
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('[MAIL] TIMEOUT: Requête annulée après 15s');
    } else {
      console.error('[MAIL] ERREUR:', error.message);
    }
    throw error;
  }
};`}
                            </pre>
                        </div>
                    </div>

                    {/* Flutter / Dart */}
                    <div style={{marginBottom: '30px'}}>
                        <h3 style={{
                            fontSize: '22px',
                            fontWeight: '700',
                            color: '#2c3e50',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{fontSize: '28px'}}>🎯</span>
                            <span>Flutter / Dart (Production)</span>
                        </h3>
                        <div style={{
                            background: '#2c3e50',
                            borderRadius: '12px',
                            padding: '24px',
                            position: 'relative',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}>
                            <button
                                onClick={() => copyCode(`import 'package:http/http.dart' as http;
import 'dart:convert';
import 'dart:developer' as developer;

Future<void> sendEmail() async {
  final url = Uri.parse('https://api.wonyasoft.com/sendInfoMail');
  final data = {
    'RefPartenaire': 'AA082',
    'TokenMail': 'VOTRE_TOKEN_MAIL',
    'DestMail': 'user@example.com',
    'Objet': 'Notification de connexion',
    'BodyMail': '<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>'
  };

  try {
    final response = await http.post(
      url,
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': 'MyApp/2.0'
      },
      body: json.encode(data),
    ).timeout(const Duration(seconds: 15));

    if (response.statusCode != 200) {
      throw Exception('HTTP \${response.statusCode}: \${response.reasonPhrase}');
    }

    final result = json.decode(response.body) as Map<String, dynamic>;

    if (result['status'] == 'S') {
      developer.log('[MAIL] Email envoyé avec succès - ID: \${result['id']}');
      developer.log('[MAIL] Destinataire: \${result['DestMail']}');
    } else {
	      developer.log('[MAIL] Échec envoi: $result', level: 1000);
      throw Exception('Statut invalide reçu');
    }
  } on TimeoutException {
    developer.log('[MAIL] TIMEOUT: Requête annulée après 15s', level: 1000);
    rethrow;
  } catch (e) {
	    developer.log('[MAIL] ERREUR: $e', level: 1000);
    rethrow;
  }
}`)}
                                style={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                    background: '#dc3545',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                📋 Copier
                            </button>
                            <pre style={{
                                color: '#fff',
                                fontSize: '14px',
                                fontFamily: 'monospace',
                                margin: 0,
                                overflowX: 'auto',
                                lineHeight: '1.6'
                            }}>
{`import 'package:http/http.dart' as http;
import 'dart:convert';
import 'dart:developer' as developer;

Future<void> sendEmail() async {
  final url = Uri.parse('https://api.wonyasoft.com/sendInfoMail');
  final data = {
    'RefPartenaire': 'AA082',
    'TokenMail': 'VOTRE_TOKEN_MAIL',
    'DestMail': 'user@example.com',
    'Objet': 'Notification de connexion',
    'BodyMail': '<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>'
  };

  try {
    final response = await http.post(
      url,
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': 'MyApp/2.0'
      },
      body: json.encode(data),
    ).timeout(const Duration(seconds: 15));

    if (response.statusCode != 200) {
      throw Exception('HTTP \${response.statusCode}: \${response.reasonPhrase}');
    }

    final result = json.decode(response.body) as Map<String, dynamic>;

    if (result['status'] == 'S') {
      developer.log('[MAIL] Email envoyé avec succès - ID: \${result['id']}');
      developer.log('[MAIL] Destinataire: \${result['DestMail']}');
    } else {
	      developer.log('[MAIL] Échec envoi: $result', level: 1000);
      throw Exception('Statut invalide reçu');
    }
  } on TimeoutException {
    developer.log('[MAIL] TIMEOUT: Requête annulée après 15s', level: 1000);
    rethrow;
  } catch (e) {
	    developer.log('[MAIL] ERREUR: $e', level: 1000);
    rethrow;
  }
}`}
                            </pre>
                        </div>
                    </div>

                    {/* Python 3.11+ */}
                    <div style={{marginBottom: '30px'}}>
                        <h3 style={{
                            fontSize: '22px',
                            fontWeight: '700',
                            color: '#2c3e50',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{fontSize: '28px'}}>🐍</span>
                            <span>Python 3.11+ (Production)</span>
                        </h3>
                        <div style={{
                            background: '#2c3e50',
                            borderRadius: '12px',
                            padding: '24px',
                            position: 'relative',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}>
                            <button
                                onClick={() => copyCode(`import requests
import logging
from requests.exceptions import Timeout, ConnectionError

logging.basicConfig(level=logging.INFO, format='%(asctime)s - %(message)s')

def send_email():
    url = 'https://api.wonyasoft.com/sendInfoMail'
    data = {
        'RefPartenaire': 'AA082',
        'TokenMail': 'VOTRE_TOKEN_MAIL',
        'DestMail': 'user@example.com',
        'Objet': 'Notification de connexion',
        'BodyMail': '<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>'
    }
    headers = {
        'Content-Type': 'application/json',
        'User-Agent': 'MyApp/2.0'
    }

    try:
        response = requests.post(url, json=data, headers=headers, timeout=15)
        response.raise_for_status()
        
        result = response.json()
        
        if result.get('status') == 'S':
            logging.info(f"[MAIL] Email envoyé avec succès - ID: {result.get('id')}")
            logging.info(f"[MAIL] Destinataire: {result.get('DestMail')}")
            return result
        else:
            logging.error(f"[MAIL] Échec envoi: {result}")
            raise ValueError('Statut invalide reçu')
            
    except Timeout:
        logging.error('[MAIL] TIMEOUT: Requête annulée après 15s')
        raise
    except ConnectionError:
        logging.error('[MAIL] ERREUR DE CONNEXION: Impossible de joindre le serveur')
        raise
    except Exception as e:
        logging.error(f'[MAIL] ERREUR: {str(e)}')
        raise

if __name__ == '__main__':
    send_email()`)}
                                style={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                    background: '#dc3545',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                📋 Copier
                            </button>
                            <pre style={{
                                color: '#fff',
                                fontSize: '14px',
                                fontFamily: 'monospace',
                                margin: 0,
                                overflowX: 'auto',
                                lineHeight: '1.6'
                            }}>
{`import requests
import logging
from requests.exceptions import Timeout, ConnectionError

logging.basicConfig(level=logging.INFO, format='%(asctime)s - %(message)s')

def send_email():
    url = 'https://api.wonyasoft.com/sendInfoMail'
    data = {
        'RefPartenaire': 'AA082',
        'TokenMail': 'VOTRE_TOKEN_MAIL',
        'DestMail': 'user@example.com',
        'Objet': 'Notification de connexion',
        'BodyMail': '<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>'
    }
    headers = {
        'Content-Type': 'application/json',
        'User-Agent': 'MyApp/2.0'
    }

    try:
        response = requests.post(url, json=data, headers=headers, timeout=15)
        response.raise_for_status()
        
        result = response.json()
        
        if result.get('status') == 'S':
            logging.info(f"[MAIL] Email envoyé avec succès - ID: {result.get('id')}")
            logging.info(f"[MAIL] Destinataire: {result.get('DestMail')}")
            return result
        else:
            logging.error(f"[MAIL] Échec envoi: {result}")
            raise ValueError('Statut invalide reçu')
            
    except Timeout:
        logging.error('[MAIL] TIMEOUT: Requête annulée après 15s')
        raise
    except ConnectionError:
        logging.error('[MAIL] ERREUR DE CONNEXION: Impossible de joindre le serveur')
        raise
    except Exception as e:
        logging.error(f'[MAIL] ERREUR: {str(e)}')
        raise

if __name__ == '__main__':
    send_email()`}
                            </pre>
                        </div>
                    </div>

                    {/* Windev */}
                    <div style={{marginBottom: '30px'}}>
                        <h3 style={{
                            fontSize: '22px',
                            fontWeight: '700',
                            color: '#2c3e50',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{fontSize: '28px'}}>🔷</span>
                            <span>Windev (W-Langage Production)</span>
                        </h3>
                        <div style={{
                            background: '#2c3e50',
                            borderRadius: '12px',
                            padding: '24px',
                            position: 'relative',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}>
                            <button
                                onClick={() => copyCode(`// Windev W-Langage - Envoi Email API (Production 2026)
PROCEDURE EnvoyerEmail()

sURL est une chaine = "https://api.wonyasoft.com/sendInfoMail"
jData est un JSON
rep est un httpRequête

// Préparation des données
jData.RefPartenaire = "AA082"
jData.TokenMail = "VOTRE_TOKEN_MAIL"
jData.DestMail = "user@example.com"
jData.Objet = "Notification de connexion"
jData.BodyMail = "<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>"

// Configuration de la requête HTTP
rep..URL = sURL
rep..ContentType = "application/json"
rep..Timeout = 15

// Envoi de la requête POST
SI PAS HTTPEnvoie(rep, httpPost, VariantVersJSON(jData)) ALORS
    Trace("[MAIL] ERREUR HTTP: " + ErreurInfo(errComplet))
    RENVOYER Faux
FIN

// Vérification du code de statut HTTP
SI rep..CodeRéponse <> 200 ALORS
    Trace("[MAIL] ERREUR: Code HTTP " + rep..CodeRéponse)
    RENVOYER Faux
FIN

// Décodage de la réponse JSON
jReponse est un JSON = JSONVersVariant(rep..Contenu)

// Validation du statut de la réponse
SI jReponse.status = "S" ALORS
    Trace("[MAIL] Email envoyé avec succès - ID: " + jReponse.id)
    Trace("[MAIL] Destinataire: " + jReponse.DestMail)
    RENVOYER Vrai
SINON
    Trace("[MAIL] Échec envoi - Réponse: " + rep..Contenu)
    RENVOYER Faux
FIN`)}
                                style={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                    background: '#dc3545',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                📋 Copier
                            </button>
                            <pre style={{
                                color: '#fff',
                                fontSize: '14px',
                                fontFamily: 'monospace',
                                margin: 0,
                                overflowX: 'auto',
                                lineHeight: '1.6'
                            }}>
{`// Windev W-Langage - Envoi Email API (Production 2026)
PROCEDURE EnvoyerEmail()

sURL est une chaine = "https://api.wonyasoft.com/sendInfoMail"
jData est un JSON
rep est un httpRequête

// Préparation des données
jData.RefPartenaire = "AA082"
jData.TokenMail = "VOTRE_TOKEN_MAIL"
jData.DestMail = "user@example.com"
jData.Objet = "Notification de connexion"
jData.BodyMail = "<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>"

// Configuration de la requête HTTP
rep..URL = sURL
rep..ContentType = "application/json"
rep..Timeout = 15

// Envoi de la requête POST
SI PAS HTTPEnvoie(rep, httpPost, VariantVersJSON(jData)) ALORS
    Trace("[MAIL] ERREUR HTTP: " + ErreurInfo(errComplet))
    RENVOYER Faux
FIN

// Vérification du code de statut HTTP
SI rep..CodeRéponse <> 200 ALORS
    Trace("[MAIL] ERREUR: Code HTTP " + rep..CodeRéponse)
    RENVOYER Faux
FIN

// Décodage de la réponse JSON
jReponse est un JSON = JSONVersVariant(rep..Contenu)

// Validation du statut de la réponse
SI jReponse.status = "S" ALORS
    Trace("[MAIL] Email envoyé avec succès - ID: " + jReponse.id)
    Trace("[MAIL] Destinataire: " + jReponse.DestMail)
    RENVOYER Vrai
SINON
    Trace("[MAIL] Échec envoi - Réponse: " + rep..Contenu)
    RENVOYER Faux
FIN`}
                            </pre>
                        </div>
                    </div>

                    {/* cURL */}
                    <div style={{marginBottom: '30px'}}>
                        <h3 style={{
                            fontSize: '22px',
                            fontWeight: '700',
                            color: '#2c3e50',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{fontSize: '28px'}}>💻</span>
                            <span>cURL (Terminal / Production)</span>
                        </h3>
                        <div style={{
                            background: '#2c3e50',
                            borderRadius: '12px',
                            padding: '24px',
                            position: 'relative',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}>
                            <button
                                onClick={() => copyCode(`curl -X POST https://api.wonyasoft.com/sendInfoMail \\
  --connect-timeout 15 \\
  --max-time 30 \\
  -H "Content-Type: application/json" \\
  -H "User-Agent: MyApp/2.0" \\
  -d '{
    "RefPartenaire": "AA082",
    "TokenMail": "VOTRE_TOKEN_MAIL",
    "DestMail": "user@example.com",
    "Objet": "Notification de connexion",
    "BodyMail": "<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>"
  }' \\
  -w "\\n[MAIL] Code HTTP: %{http_code} | Temps: %{time_total}s\\n" \\
  -s -S`)}
                                style={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                    background: '#dc3545',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                📋 Copier
                            </button>
                            <pre style={{
                                color: '#fff',
                                fontSize: '14px',
                                fontFamily: 'monospace',
                                margin: 0,
                                overflowX: 'auto',
                                lineHeight: '1.6'
                            }}>
{`curl -X POST https://api.wonyasoft.com/sendInfoMail \\
  --connect-timeout 15 \\
  --max-time 30 \\
  -H "Content-Type: application/json" \\
  -H "User-Agent: MyApp/2.0" \\
  -d '{
    "RefPartenaire": "AA082",
    "TokenMail": "VOTRE_TOKEN_MAIL",
    "DestMail": "user@example.com",
    "Objet": "Notification de connexion",
    "BodyMail": "<h1>Bonjour</h1><p>Nouvelle connexion détectée sur votre compte.</p>"
  }' \\
  -w "\\n[MAIL] Code HTTP: %{http_code} | Temps: %{time_total}s\\n" \\
  -s -S`}
                            </pre>
                        </div>
                    </div>
                </section>

                {/* Réponses */}
                <section style={{marginBottom: '50px'}}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        marginBottom: '24px',
                        paddingBottom: '16px',
                        borderBottom: '3px solid #dc3545'
                    }}>
                        <span style={{fontSize: '40px'}}>📥</span>
                        <h2 style={{fontSize: '32px', fontWeight: '700', color: '#2c3e50', margin: 0}}>
                            Réponses de l'API
                        </h2>
                    </div>

                    {/* Succès */}
                    <div style={{marginBottom: '30px'}}>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#28a745',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{fontSize: '28px'}}>✅</span>
                            <span>Succès (Status: 200)</span>
                        </h3>
                        <div style={{
                            background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            border: '2px solid #28a745',
                            boxShadow: '0 4px 12px rgba(40,167,69,0.2)'
                        }}>
                            <pre style={{
                                color: '#155724',
                                fontSize: '15px',
                                fontFamily: 'monospace',
                                margin: 0,
                                lineHeight: '1.8'
                            }}>
{`{
  "id": "000000000000000001",
  "status": "S",
  "DestMail": "email@example.com"
}`}
                            </pre>
                        </div>
                    </div>

                    {/* Erreur */}
                    <div>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#dc3545',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{fontSize: '28px'}}>❌</span>
                            <span>Erreur (Status: 400/500)</span>
                        </h3>
                        <div style={{
                            background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            border: '2px solid #dc3545',
                            boxShadow: '0 4px 12px rgba(220,53,69,0.2)'
                        }}>
                            <pre style={{
                                color: '#721c24',
                                fontSize: '15px',
                                fontFamily: 'monospace',
                                margin: 0,
                                lineHeight: '1.8'
                            }}>
{`{
  "error": "Message d'erreur",
  "details": "Description détaillée"
}`}
                            </pre>
                        </div>
                    </div>
                </section>

                {/* Points importants */}
                <section style={{marginBottom: '50px'}}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        marginBottom: '24px',
                        paddingBottom: '16px',
                        borderBottom: '3px solid #dc3545'
                    }}>
                        <span style={{fontSize: '40px'}}>⚠️</span>
                        <h2 style={{fontSize: '32px', fontWeight: '700', color: '#2c3e50', margin: 0}}>
                            Points Importants & Bonnes Pratiques
                        </h2>
                    </div>
                    <div style={{
                        background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                        borderRadius: '16px',
                        padding: '30px',
                        border: '2px solid #ffc107',
                        boxShadow: '0 4px 12px rgba(255,193,7,0.2)'
                    }}>
                        <ul style={{
                            margin: 0,
                            paddingLeft: '30px',
                            color: '#856404',
                            fontSize: '16px',
                            lineHeight: '2.4'
                        }}>
                            <li><strong>✅ Validation Stricte</strong> : Tous les paramètres sont obligatoires - aucun champ ne peut être vide</li>
                            <li><strong>📧 Format Email</strong> : L'adresse email doit être valide et respecter le format standard (ex: user@domain.com)</li>
                            <li><strong>💳 Gestion du Solde</strong> : Votre SoldeMails doit être ≥ 1 pour envoyer un email. Rechargez depuis le Dashboard</li>
                            <li><strong>🎨 Support HTML</strong> : Le corps de l'email supporte entièrement le HTML/CSS pour des designs professionnels</li>
                            <li><strong>💰 Coût par Email</strong> : Chaque envoi réussi décrémente automatiquement votre solde de 1 crédit</li>
                            <li><strong>📊 Historique Complet</strong> : Chaque envoi est enregistré dans la base MailSent avec timestamp et statut</li>
                            <li><strong>🔑 Sécurité Token</strong> : Générez votre TokenMail depuis le Dashboard - ne le partagez jamais publiquement</li>
                            <li><strong>⚡ Temps de Réponse</strong> : Latence moyenne {'<'} 2 secondes - idéal pour les notifications en temps réel</li>
                            <li><strong>🔒 HTTPS Obligatoire</strong> : Toutes les requêtes doivent utiliser le protocole HTTPS pour la sécurité</li>
                            <li><strong>📈 Limite de Taux</strong> : Pas de limite stricte mais optimisez vos envois pour de meilleures performances</li>
                            <li><strong>🌍 UTF-8</strong> : Encodage UTF-8 supporté pour tous les caractères spéciaux et langues internationales</li>
                            <li><strong>📝 Headers JSON</strong> : N'oubliez pas le header Content-Type: application/json dans vos requêtes</li>
                        </ul>
                    </div>
                </section>

                {/* Section Métriques */}
                <section style={{marginBottom: '50px'}}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        marginBottom: '24px',
                        paddingBottom: '16px',
                        borderBottom: '3px solid #dc3545'
                    }}>
                        <span style={{fontSize: '40px'}}>📈</span>
                        <h2 style={{fontSize: '32px', fontWeight: '700', color: '#2c3e50', margin: 0}}>
                            Performance & Fiabilité
                        </h2>
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '20px'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            textAlign: 'center',
                            color: '#fff',
                            boxShadow: '0 8px 24px rgba(102,126,234,0.3)'
                        }}>
                            <div style={{fontSize: '48px', fontWeight: '700', marginBottom: '10px'}}>99.9%</div>
                            <div style={{fontSize: '16px', fontWeight: '600'}}>Taux de Livraison</div>
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            textAlign: 'center',
                            color: '#fff',
                            boxShadow: '0 8px 24px rgba(240,147,251,0.3)'
                        }}>
                            <div style={{fontSize: '48px', fontWeight: '700', marginBottom: '10px'}}>{'<'}2s</div>
                            <div style={{fontSize: '16px', fontWeight: '600'}}>Latence Moyenne</div>
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            textAlign: 'center',
                            color: '#fff',
                            boxShadow: '0 8px 24px rgba(79,172,254,0.3)'
                        }}>
                            <div style={{fontSize: '48px', fontWeight: '700', marginBottom: '10px'}}>24/7</div>
                            <div style={{fontSize: '16px', fontWeight: '600'}}>Disponibilité</div>
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            borderRadius: '16px',
                            padding: '30px',
                            textAlign: 'center',
                            color: '#fff',
                            boxShadow: '0 8px 24px rgba(250,112,154,0.3)'
                        }}>
                            <div style={{fontSize: '48px', fontWeight: '700', marginBottom: '10px'}}>∞</div>
                            <div style={{fontSize: '16px', fontWeight: '600'}}>Scalabilité</div>
                        </div>
                    </div>
                </section>

                {/* Support */}
                <section>
                    <div style={{
                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                        borderRadius: '20px',
                        padding: '40px',
                        color: '#fff',
                        textAlign: 'center',
                        boxShadow: '0 10px 40px rgba(220,53,69,0.3)'
                    }}>
                        <span style={{fontSize: '60px', display: 'block', marginBottom: '20px'}}>💬</span>
                        <h3 style={{fontSize: '28px', fontWeight: '700', marginBottom: '12px'}}>
                            Besoin d'aide ?
                        </h3>
                        <p style={{
                            fontSize: '16px',
                            marginBottom: '30px',
                            opacity: 0.95,
                            maxWidth: '600px',
                            margin: '0 auto 30px'
                        }}>
                            Notre équipe est là pour vous accompagner dans l'intégration de l'API Mail
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '16px',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <a
                                className="mail-doc-whatsapp"
                                href="https://wa.me/243892672472"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    background: '#25D366',
                                    color: '#fff',
                                    padding: '16px 32px',
                                    borderRadius: '12px',
                                    textDecoration: 'none',
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    boxShadow: '0 4px 12px rgba(37,211,102,0.3)',
                                    transition: 'transform 0.3s'
                                }}
                                onMouseEnter={(e) => e.target.style.transform = 'translateY(-3px)'}
                                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                            >
                                <span style={{fontSize: '24px'}}>📱</span>
                                <span>WhatsApp Support</span>
                            </a>
                            <a
                                href="mailto:info@wonyasoft.com"
                                style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    padding: '16px 32px',
                                    borderRadius: '12px',
                                    textDecoration: 'none',
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    border: '2px solid rgba(255,255,255,0.3)',
                                    backdropFilter: 'blur(10px)',
                                    transition: 'all 0.3s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = 'rgba(255,255,255,0.3)';
                                    e.target.style.transform = 'translateY(-3px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'rgba(255,255,255,0.2)';
                                    e.target.style.transform = 'translateY(0)';
                                }}
                            >
                                <span style={{fontSize: '24px'}}>📧</span>
                                <span>info@wonyasoft.com</span>
                            </a>
                        </div>
                    </div>
                </section>
            </div>

            {/* Footer */}
            <div style={{
                maxWidth: '1200px',
                margin: '40px auto 0',
                textAlign: 'center',
                color: 'rgba(255,255,255,0.9)',
                fontSize: '14px'
            }}>
                <p>© 2025 WonyaSoft - Tous droits réservés</p>
            </div>
            </div>
        </div>
    );
};

export default MailDoc;
